import { htmlToText } from "html-to-text";
import invariant from "invariant";
import { micromark } from "micromark";

import { callTimestampedLink } from "../../main/components/CallNotes/utils";
import {
  AiQuestionNoteAnswerItem,
  Ats,
  CallAiSummaryFormat,
  NotePartsFragment,
  NotesForScorecardQuery,
  NotesForScorecardV3Query,
} from "../../main/graphql";
import { buildCallTimestampLink } from "../call";
import {
  buildExtensionNote,
  formatNote as formatNoteBase,
  gatherNotes as gatherNotesBase,
} from "./formatting";
import {
  CueNotes,
  ExtensionNote,
  FillScorecardParams,
  FillScorecardPayload,
  QuestionNotes,
} from "./types";
import {
  DEFAULT_AI_NOTES_HEADER,
  HIDE_AI_NOTES_HEADER,
  INTERVIEWER_NOTES_HEADER,
  isSupportedAts,
  isV3Call,
} from "./utils";

/**
 * This function takes all of the call's notes and formats them for the extension.
 * The format for a single question looks like this:
 *
 * ```
 * Notes from interviewer:
 *
 * • Interviewer marked question at 10:12
 * • Provided solid feedback 10:30
 *
 * ✨ AI summarized notes:
 *
 * Candidate discussed: work history 11:15
 * • Candidate previously worked at XYZ corp
 * • Left after changes in management
 *
 * Tell me about your last project 12:30
 * • Previously worked on an AI chatbot for customer service
 * • Reduced on-call load by 35%
 * ```
 *
 * For the scorecard template, we omit the AI headers since they will
 * always be the same as the item being pasted into on the ATS page, so:
 * ```
 * Notes from interviewer:
 *
 * • Interviewer marked question at 10:12
 * • Provided solid feedback 10:30
 *
 * ✨ AI summarized notes:
 *
 * • Candidate previously worked at XYZ corp
 * • Left after changes in management
 * • Previously worked on an AI chatbot for customer service
 * • Reduced on-call load by 35%
 * ```
 *
 */
export function getScorecardData({
  call,
  ats,
  autofill,
  showConfetti,
  latestFormatEnabled,
  hideAiHeaders,
  includeTimestamps,
}: FillScorecardParams): FillScorecardPayload {
  const isGreenhouse = ats === Ats.Greenhouse;
  const isAshby = ats === Ats.Ashby;

  invariant(
    isSupportedAts(ats),
    `"${ats}" is not a supported ATS for fill scorecard`
  );

  const AI_NOTES_HEADER = hideAiHeaders
    ? HIDE_AI_NOTES_HEADER
    : DEFAULT_AI_NOTES_HEADER;

  const isHtmlSupported = isGreenhouse || isAshby;

  const isAiScorecardTemplate =
    call?.aiNotesCustomFormat?.format === CallAiSummaryFormat.Scorecard;
  const isAiCustomTemplate =
    call?.aiNotesCustomFormat?.format === CallAiSummaryFormat.Custom &&
    !!call?.aiScorecardCustomSummary;

  const callId = call?.id;
  const generalNotes = gatherNotes(call?.generalNotes);
  if (isAiCustomTemplate) {
    prependCustomSummary();
  }

  let questionNotes: QuestionNotes[];

  if (isV3Call(call)) {
    questionNotes = gatherScorecardNotes(call?.scorecard);
  } else {
    questionNotes = gatherQuestionNotes(call?.questions ?? []);
  }

  mergeAiScorecardQuestionNotes(questionNotes);

  return {
    generalNotes,
    questionNotes,
    autofill,
    showConfetti,
    replaceHeaders: [INTERVIEWER_NOTES_HEADER, AI_NOTES_HEADER],
  };

  function formatNote({
    parts,
    includeBullet = true,
    isAtsSingleLineInput,
  }: {
    parts: string[];
    isAtsSingleLineInput?: boolean;
    includeBullet?: boolean;
  }): string {
    return formatNoteBase({
      parts,
      includeBullet,
      isAtsSingleLineInput,
      isHtmlSupported,
    });
  }

  /**
   * Gathers manual call notes into an `ExtensionNote`
   */
  function gatherNotes(
    notes: NotePartsFragment[] | undefined,
    timestamp?: number | null,
    description?: string,
    isAtsSingleLineInput?: boolean
  ): ExtensionNote {
    return gatherNotesBase({
      callId,
      notes,
      timestamp,
      description,
      isAtsSingleLineInput,
      includeTimestamps,
      isHtmlSupported,
      latestFormatEnabled,
    });
  }

  /**
   * Prepends the custom AI summary to the general notes
   */
  function prependCustomSummary(): void {
    const { html, text } = getAiSummaryContent(
      call?.aiScorecardCustomSummary || ""
    );
    const contentToPrepend = isHtmlSupported ? html : text;
    generalNotes.text = contentToPrepend + generalNotes.text;
    generalNotes.generalText = contentToPrepend + generalNotes.generalText;
    generalNotes.count += 1;
  }

  /**
   * Gathers AI notes into an `ExtensionNote`
   */
  function gatherAiNoteAnswerItems(
    notes: Pick<AiQuestionNoteAnswerItem, "id" | "currentText">[],
    questionHeader: string,
    startTime: number,
    isAtsSingleLineInput: boolean,
    callId?: string
  ): ExtensionNote {
    const result: CueNotes = {
      description: questionHeader,
      cues: [],
      notes: [],
    };

    const shouldAddHeader = notes.length > 0 && !isAiScorecardTemplate;
    if (shouldAddHeader) {
      /**
       * Add the summary header (e.g. "Candidate discussed: ...") for
       * non-scorecard templates
       */
      const summaryHeaderItems = latestFormatEnabled
        ? [questionHeader]
        : ["✨", questionHeader];

      if (callId && isHtmlSupported && includeTimestamps) {
        const link = buildCallTimestampLink(callId, startTime);
        summaryHeaderItems.push(link);
      }
      result.notes.push(
        formatNote({
          parts: summaryHeaderItems,
          includeBullet: false,
          isAtsSingleLineInput,
        })
      );
    }

    if (notes.length > 0) {
      notes.forEach((note) => {
        const text = note.currentText;
        result.notes.push(formatNote({ parts: [text], isAtsSingleLineInput }));
      });
    }

    const extensionNote = buildExtensionNote(result, {
      isAtsSingleLineInput,
      header: AI_NOTES_HEADER,
      isHtmlSupported,
      latestFormatEnabled,
    });

    if (shouldAddHeader) {
      /** Decrement by 1 so the header (`summaryHeaderItems`) is not counted */
      extensionNote.count -= 1;
    }

    return extensionNote;
  }

  /**
   * Gathers (manual) guide item notes for an `iaVersion = 2` call
   */
  function gatherQuestionNotes(
    questions: NonNullable<NotesForScorecardQuery["call"]>["questions"]
  ): QuestionNotes[] {
    const result: QuestionNotes[] = questions.map((question) => {
      const questionNote: QuestionNotes = {
        id: question.id,
        notes: gatherNotes(
          question.questionNotes,
          null,
          question.description ?? undefined,
          question.isAtsSingleLineInput
        ),
        text: getQuestionText(question.description ?? ""),
      };
      return questionNote;
    });

    return result;
  }

  /**
   * Gathers (manual) guide item notes for an `iaVersion = 3` call
   */
  function gatherScorecardNotes(
    scorecard: NonNullable<NotesForScorecardV3Query["call"]>["scorecard"]
  ): QuestionNotes[] {
    const result: QuestionNotes[] =
      scorecard?.questions.map((question) => {
        const questionNote: QuestionNotes = {
          id: question.id,
          notes: gatherNotes(
            question.questionNotes,
            question.marked ? question.markedTime : undefined,
            question.itemPlainText,
            question.isAtsSingleLineInput
          ),
          text: getQuestionText(question.itemPlainText ?? ""),
        };
        return questionNote;
      }) ?? [];

    return result;
  }

  /**
   * Absorbs AI notes into the corresponding (manual) guide item notes
   */
  function mergeAiScorecardQuestionNotes(result: QuestionNotes[]): void {
    const resultMap = new Map(
      result.map((questionNotes) => [questionNotes.text, questionNotes])
    );
    const aiScorecardQuestionNotes = call?.aiScorecardQuestionNotes ?? [];
    aiScorecardQuestionNotes.forEach((note) => {
      const questionNote: QuestionNotes = {
        id: note.id,
        notes: gatherAiNoteAnswerItems(
          note.callAiNoteAnswerItems,
          note.aiQuestion ?? "",
          note.startTime,
          note.isAtsSingleLineInput,
          callId
        ),
        text: getQuestionText(note.scorecardQuestion ?? ""),
      };

      // merge with existing notes with the same Scorecard Question
      const currentQuestionNote = resultMap.get(questionNote.text);
      if (!currentQuestionNote) {
        resultMap.set(questionNote.text, questionNote);
        result.push(questionNote);
      } else {
        // Update in place current note
        currentQuestionNote.notes.count += questionNote.notes.count;
        if (currentQuestionNote.notes.text === "") {
          currentQuestionNote.notes.text = questionNote.notes.text;
        } else {
          currentQuestionNote.notes.text =
            currentQuestionNote.notes.text.concat(
              isHtmlSupported
                ? "<br><br>"
                : note.isAtsSingleLineInput
                ? " | "
                : "\n\n",
              questionNote.notes.text
            );
        }
        if (currentQuestionNote.notes.generalText === "") {
          currentQuestionNote.notes.generalText =
            questionNote.notes.generalText;
        } else {
          currentQuestionNote.notes.generalText =
            currentQuestionNote.notes.generalText.concat(
              isHtmlSupported
                ? "<br><br>"
                : note.isAtsSingleLineInput
                ? " | "
                : "\n\n",
              questionNote.notes.generalText
            );
        }
      }
    });
  }

  /**
   * Adds a custom AI summary to the notes
   */
  function getAiSummaryContent(customSummary: string): {
    html: string;
    text: string;
  } {
    const CUSTOM_SUMMARY_HEADER = "Custom AI Summary:";
    const htmlSummary = convertMarkdownToHtml(customSummary);
    const plainTextSummary = htmlToText(htmlSummary, {
      preserveNewlines: true,
      selectors: [
        { selector: "a", format: "inline" },
        {
          selector: "ul",
          options: {
            itemPrefix: "• ",
          },
        },
      ],
    });
    const html = `<strong>${CUSTOM_SUMMARY_HEADER}</strong><br>${htmlSummary}<br><br>`;
    const text = `${CUSTOM_SUMMARY_HEADER}\n\n${plainTextSummary}\n\n`;

    return {
      html,
      text,
    };
  }

  /**
   * Converts markdown to HTML, handling timestamp links
   */
  function convertMarkdownToHtml(markdown: string): string {
    // First, process timestamp links with pattern [text](?t=timestamp)
    // These are special BrightHire links that need to be handled before micromark processing
    const timestampLinkRegex = /\[([^\]]+)\]\(\?t=(\d+)\)/g;
    const processedMarkdown = markdown.replace(
      timestampLinkRegex,
      (match, text, timestamp) => {
        // Generate the proper URL for the timestamp link
        const url = callTimestampedLink(callId || "", parseInt(timestamp, 10));
        // Return the markdown with the updated URL
        return `[${text}](${url})`;
      }
    );

    // Convert the processed markdown to HTML using micromark
    const html = micromark(processedMarkdown);
    return html;
  }
}

const dummyEl = document.createElement("span");
const getQuestionText = (description: string): string => {
  dummyEl.innerText = description;
  return dummyEl.innerText;
};
